import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableList, type ListItemComponentProps } from 'common/common/SortableList';
import { IdeaFieldTypes, MaxFieldOptionCharLength, MaxFieldOptions } from 'common/constants/ideas';
import generateRandomID from 'common/generateRandomID';
import { FieldTypeDataMap } from 'common/subdomain/admin/v2/constants';
import type {
  CustomFieldFormState,
  FieldFormState,
} from 'common/subdomain/admin/v2/hooks/useIdeaFieldForm';
import Button from 'common/uiV3/Button';
import Field from 'common/uiV3/Field';
import Flex from 'common/uiV3/Flex';
import Input from 'common/uiV3/Input';
import { Select } from 'common/uiV3/Select';
import { FormInput, GripVertical, Trash } from 'lucide-react';

type OptionWithID = {
  id: string;
  value: string;
};

type BaseFieldInputsProps = {
  formState: FieldFormState;
  setValue: (field: keyof CustomFieldFormState, value: string | string[]) => void;
  types?: IdeaFieldTypes[];
  readOnly: boolean;
  loading: boolean;
};

const TypeOptions = Object.keys(FieldTypeDataMap).map((type) => {
  const { label, icon: Icon } = FieldTypeDataMap[type as IdeaFieldTypes];

  return {
    value: type,
    label: (
      <Select.OptionLabel>
        {<Icon />} {label}
      </Select.OptionLabel>
    ),
  };
});

export default function BaseFieldInputs({
  types = Object.values(IdeaFieldTypes),
  formState,
  setValue,
  readOnly,
  loading,
}: BaseFieldInputsProps) {
  const optionIdsRef = useRef<string[]>([]);
  const handleKeyDownRef = useRef<(e: React.KeyboardEvent<HTMLInputElement>) => void>(() => {});
  const inputRefsRef = useRef<Map<string, HTMLInputElement>>(new Map());
  const shouldFocusNewOptionRef = useRef(false);

  const selectedTypeOption = TypeOptions.find((option) => option.value === formState.type);
  const showOptionsInput =
    formState.type === IdeaFieldTypes.dropdown || formState.type === IdeaFieldTypes.multiselect;
  const hasOptionsList = 'options' in formState;

  const optionsWithIDs = useMemo(() => {
    if (!hasOptionsList || !formState.options) {
      optionIdsRef.current = [];
      return [];
    }

    while (optionIdsRef.current.length < formState.options.length) {
      optionIdsRef.current.push(generateRandomID());
    }

    if (optionIdsRef.current.length > formState.options.length) {
      optionIdsRef.current = optionIdsRef.current.slice(0, formState.options.length);
    }

    return formState.options.map((value, index) => ({
      id: optionIdsRef.current[index],
      value,
    }));
    // @ts-expect-error - formState.options
  }, [formState?.options, hasOptionsList]);

  const optionsWithIDsRef = useRef<OptionWithID[]>([]);
  optionsWithIDsRef.current = optionsWithIDs;

  const handleOptionChangeRef = useRef<(id: string, value: string) => void>(() => {});
  handleOptionChangeRef.current = (id: string, value: string) => {
    if (!hasOptionsList) {
      return;
    }
    const updatedOptions = optionsWithIDsRef.current.map((opt) =>
      opt.id === id ? { ...opt, value } : opt,
    );
    setValue(
      'options',
      updatedOptions.map((opt) => opt.value),
    );
  };

  const handleDeleteOptionRef = useRef<(id: string) => void>(() => {});
  handleDeleteOptionRef.current = (id: string) => {
    if (!hasOptionsList) {
      return;
    }
    const updatedOptions = optionsWithIDsRef.current.filter((opt) => opt.id !== id);
    setValue(
      'options',
      updatedOptions.map((opt) => opt.value),
    );
  };

  const handleAddOption = () => {
    if (!hasOptionsList) {
      return;
    }
    shouldFocusNewOptionRef.current = true;
    const existingOptions = formState.options ?? [];
    setValue('options', [...existingOptions, '']);
  };

  const handleItemsChanged = useCallback(
    (items: OptionWithID[]) => {
      if (!hasOptionsList) {
        return;
      }
      optionIdsRef.current = items.map((optionItem) => optionItem.id);
      setValue(
        'options',
        items.map((optionItem) => optionItem.value),
      );
    },
    [hasOptionsList, setValue],
  );

  const getOptionId = useCallback((optionItem: OptionWithID) => optionItem.id, []);

  const getIndexRef = useRef<(id: string) => number>(() => 0);
  getIndexRef.current = (id: string) => optionsWithIDsRef.current.findIndex((opt) => opt.id === id);

  handleKeyDownRef.current = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
    }
  };

  const registerInputRef = useCallback((id: string, element: HTMLInputElement | null) => {
    if (element) {
      inputRefsRef.current.set(id, element);
    } else {
      inputRefsRef.current.delete(id);
    }
  }, []);

  useEffect(() => {
    if (shouldFocusNewOptionRef.current && optionsWithIDs.length > 0) {
      shouldFocusNewOptionRef.current = false;
      const lastItem = optionsWithIDs[optionsWithIDs.length - 1];
      const lastInput = inputRefsRef.current.get(lastItem.id);
      setTimeout(() => lastInput?.focus(), 0);
    }
  }, [optionsWithIDs]);

  const OptionItemComponent = useMemo(
    () =>
      React.forwardRef<HTMLLIElement, ListItemComponentProps<OptionWithID, HTMLLIElement>>(
        ({ item, handleProps, style }, ref) => {
          const index = getIndexRef.current(item.id);

          return (
            <li ref={ref} style={style} className="flex items-center gap-2">
              <Flex {...handleProps} tabIndex={-1}>
                <Button
                  variant="unstyled"
                  size="small"
                  iconOnly
                  className="w-3.5 hover:cursor-grab active:cursor-grabbing text-muted-foreground">
                  <GripVertical />
                </Button>
              </Flex>
              <span className="w-5 flex text-base items-center justify-center">{index + 1}</span>
              <Input
                ref={(el) => registerInputRef(item.id, el)}
                onChange={(value) => handleOptionChangeRef.current(item.id, value)}
                onKeyDown={handleKeyDownRef.current}
                maxLength={MaxFieldOptionCharLength}
                placeholder="Option name"
                value={item.value}
              />
              {optionsWithIDsRef.current.length > 1 && (
                <Button
                  aria-label="Remove option"
                  color="error"
                  onClick={() => handleDeleteOptionRef.current(item.id)}
                  size="small"
                  variant="plain"
                  iconOnly>
                  <Trash />
                </Button>
              )}
            </li>
          );
        },
      ),
    [registerInputRef],
  );

  return (
    <>
      <Field>
        <Field.Label required>Field Name</Field.Label>
        <Field.Content>
          <Input
            autoFocus
            id="fieldName"
            startIcon={FormInput}
            onChange={(value) => setValue('name', value)}
            placeholder="Field name"
            value={formState.name}
          />
        </Field.Content>
      </Field>
      <Field>
        <Field.Label required>Type</Field.Label>
        <Field.Content>
          <Select
            disabled={readOnly}
            onValueChange={(value) => setValue('type', value ?? '')}
            options={TypeOptions.filter((option) => types.includes(option.value as IdeaFieldTypes))}
            placeholder="Field type"
            value={selectedTypeOption?.value ?? ''}
          />
        </Field.Content>
      </Field>
      {showOptionsInput && (
        <Field>
          <Field.Label>Options</Field.Label>
          <Field.Content className="items-start">
            <div className="space-y-2 w-full">
              <SortableList
                items={optionsWithIDs}
                onItemsChanged={handleItemsChanged}
                getId={getOptionId}
                ItemComponent={OptionItemComponent}
                dndContextProps={{
                  modifiers: [restrictToVerticalAxis],
                }}
              />
            </div>
            <Button
              variant="plain"
              disabled={(formState.options?.length ?? 0) >= MaxFieldOptions || loading}
              onClick={handleAddOption}
              size="small">
              Create new option
            </Button>
          </Field.Content>
        </Field>
      )}
    </>
  );
}
